import {
  useCallback,
  useEffect,
  useState,
  type HTMLAttributes,
  type MouseEvent,
  type ReactNode,
} from "react";
import { useMutation } from "react-query";
import { Box, IconButton, ListItem } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import useAuthentication from "../../../hooks/useAuthentication";
import usePopup from "../../../hooks/usePopup";
import { addDrugFavourite } from "../../services";
import { clinicId } from "../../../constants";

export type DrugFavoriteOption = {
  drugId: number;
  drugCode?: string;
  drugPackageName?: string;
  drugGenericName?: string;
  isFavorite?: number;
};

type UseDrugFavouriteOptions = {
  drugOptions?: DrugFavoriteOption[];
};

export default function useDrugFavourite({
  drugOptions,
}: UseDrugFavouriteOptions = {}) {
  const { showSnackbar } = usePopup();
  const { user } = useAuthentication();
  const activitySearchUserId =
    Number(user?.userId ?? user?.login_id) || null;

  const [favoriteDrugIds, setFavoriteDrugIds] = useState<Set<number>>(new Set());
  const [unfavoriteDrugIds, setUnfavoriteDrugIds] = useState<Set<number>>(
    new Set(),
  );
  const [drugListOpen, setDrugListOpen] = useState(false);

  const isDrugFavorite = useCallback(
    (option: DrugFavoriteOption) => {
      if (unfavoriteDrugIds.has(option.drugId)) return false;
      if (favoriteDrugIds.has(option.drugId)) return true;
      return Number(option.isFavorite) === 1;
    },
    [favoriteDrugIds, unfavoriteDrugIds],
  );

  useEffect(() => {
    const favoriteIds =
      drugOptions
        ?.filter((item) => Number(item.isFavorite) === 1)
        .map((item) => item.drugId) ?? [];
    if (!favoriteIds.length) return;
    setFavoriteDrugIds((prev) => {
      const next = new Set(prev);
      favoriteIds.forEach((id) => next.add(id));
      return next;
    });
    setUnfavoriteDrugIds((prev) => {
      const next = new Set(prev);
      favoriteIds.forEach((id) => next.delete(id));
      return next;
    });
  }, [drugOptions]);

  const { mutate: saveDrugFavourite, isLoading: savingDrugFavourite } =
    useMutation(addDrugFavourite, {
      onSuccess: (result, variables) => {
        if (result.responseIndicator === "success") {
          if (variables.isFavorite === 1) {
            setFavoriteDrugIds((prev) => new Set(prev).add(variables.drugId));
            setUnfavoriteDrugIds((prev) => {
              const next = new Set(prev);
              next.delete(variables.drugId);
              return next;
            });
          } else {
            setFavoriteDrugIds((prev) => {
              const next = new Set(prev);
              next.delete(variables.drugId);
              return next;
            });
            setUnfavoriteDrugIds((prev) =>
              new Set(prev).add(variables.drugId),
            );
          }
          setDrugListOpen(true);
          showSnackbar({
            message:
              result.responseMessage ||
              (variables.isFavorite === 1
                ? "Drug added to favourites."
                : "Drug removed from favourites."),
            open: true,
            variant: "success",
            duration: 2000,
          });
        } else {
          showSnackbar({
            message: result.responseMessage || "Could not update favourite drug.",
            open: true,
            variant: "warning",
            duration: 3000,
          });
        }
      },
      onError: (error: { response?: { data?: { responseMessage?: string } } }) => {
        showSnackbar({
          message:
            error?.response?.data?.responseMessage ||
            "Could not update favourite drug.",
          open: true,
          variant: "error",
          duration: 3000,
        });
      },
    });

  const handleToggleDrugFavourite = (
    option: DrugFavoriteOption,
    event: MouseEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation();
    event.preventDefault();
    setDrugListOpen(true);
    if (!activitySearchUserId) {
      showSnackbar({
        message: "User id is required to save favourite drug.",
        open: true,
        variant: "warning",
        duration: 3000,
      });
      return;
    }
    if (savingDrugFavourite) return;

    const isFavorite = isDrugFavorite(option) ? 0 : 1;
    saveDrugFavourite({
      drugId: option.drugId,
      userId: activitySearchUserId,
      clinicId,
      isFavorite,
    });
  };

  const renderDrugFavoriteAction = (option: DrugFavoriteOption) => (
    <IconButton
      size="small"
      aria-label={
        isDrugFavorite(option)
          ? "Remove drug from favourites"
          : "Add drug to favourites"
      }
      disabled={savingDrugFavourite}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onClick={(e) => handleToggleDrugFavourite(option, e)}
      sx={{ flexShrink: 0, p: 0.5 }}
    >
      {isDrugFavorite(option) ? (
        <StarIcon color="warning" sx={{ fontSize: "1.25rem" }} />
      ) : (
        <StarBorderIcon sx={{ fontSize: "1.25rem" }} />
      )}
    </IconButton>
  );

  const renderDrugOptionListItem = (
    props: HTMLAttributes<HTMLLIElement> & { key?: string | number },
    option: DrugFavoriteOption,
    children: ReactNode,
  ) => {
    const { key, ...optionProps } = props;
    return (
      <ListItem
        key={key}
        {...optionProps}
        disablePadding
        sx={{ py: 1, px: 1.5, alignItems: "flex-start" }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 0.5,
            width: "100%",
            minWidth: 0,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
            {children}
          </Box>
          <Box sx={{ flexShrink: 0, alignSelf: "center" }}>
            {renderDrugFavoriteAction(option)}
          </Box>
        </Box>
      </ListItem>
    );
  };

  return {
    drugListOpen,
    setDrugListOpen,
    isDrugFavorite,
    savingDrugFavourite,
    renderDrugOptionListItem,
    activitySearchUserId,
  };
}
